// Shared registry for user-uploaded HDF5 datasets.
//
// The pane.ts Upload button writes here when a file is picked; datasetManager
// reads to resolve blob:// URLs on switchDataset. Replaces the former
// `window.MAGVIZ_DATASET_MAP` global with a shared module reference.
//
// `set`/`delete` revoke any prior blob URL on overwrite so we don't pin
// File handles + GPU-decoded blobs in memory across re-uploads.

const _map = new Map<string, string>();

function tryRevoke(url: string | undefined): void {
    if (typeof url !== 'string') return;
    if (!url.startsWith('blob:')) return;
    try { URL.revokeObjectURL(url); } catch { /* swallow */ }
}

export interface UploadedDatasets {
    get(key: string): string | undefined;
    has(key: string): boolean;
    keys(): IterableIterator<string>;
    values(): IterableIterator<string>;
    entries(): IterableIterator<[string, string]>;
    [Symbol.iterator](): IterableIterator<[string, string]>;
    readonly size: number;
    set(key: string, url: string): UploadedDatasets;
    delete(key: string): boolean;
    clear(): void;
}

export const uploadedDatasets: UploadedDatasets = {
    get(key) { return _map.get(key); },
    has(key) { return _map.has(key); },
    keys() { return _map.keys(); },
    values() { return _map.values(); },
    entries() { return _map.entries(); },
    [Symbol.iterator]() { return _map[Symbol.iterator](); },
    get size() { return _map.size; },
    set(key, url) {
        const prev = _map.get(key);
        if (prev && prev !== url) tryRevoke(prev);
        _map.set(key, url);
        return this;
    },
    delete(key) {
        const prev = _map.get(key);
        if (prev) tryRevoke(prev);
        return _map.delete(key);
    },
    clear() {
        for (const url of _map.values()) tryRevoke(url);
        _map.clear();
    },
};
